import { useReplaceActie } from "@/entities/actie";
import { ReikwijdteGewijzigdActie } from "@/entities/actie/domain/reikwijdte-gewijzigd";
import { TrajectenGewijzigdActie } from "@/entities/actie/domain/trajecten-gewijzigd";
import { trajectenSchema, useGetDossierDetailsById } from "@/entities/dossier-details";
import { getReikwijdteLabel } from "@/features/dossier-details";
import { TrajectenEditor } from "@/features/dossier-management/components/shared";
import { Button, DialogFooter, ErrorDisplay, Form } from "@/shared/components";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm, UseFormHandleSubmit } from "react-hook-form";
import z from "zod";
import { ActieDialog, ActieDialogFooterProps, DeclineActieConfirmation } from ".";

const nieuweTrajectenSchema = z.object({
    trajecten: trajectenSchema,
});

export type NieuweTrajectenData = z.infer<typeof nieuweTrajectenSchema>;

type TrajectenOfReikwijdteGewijzigdActie = TrajectenGewijzigdActie | ReikwijdteGewijzigdActie;

type TrajectenOfReikwijdteGewijzigdActieDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    dossierId: string;
    actie: TrajectenOfReikwijdteGewijzigdActie;
};

export function TrajectenOfReikwijdteGewijzigdActieDialog({
    open,
    onOpenChange,
    dossierId,
    actie,
}: TrajectenOfReikwijdteGewijzigdActieDialogProps) {
    const { data } = useGetDossierDetailsById(dossierId);

    const form = useForm<NieuweTrajectenData>({
        resolver: zodResolver(nieuweTrajectenSchema),
        defaultValues: {
            trajecten: actie.nieuweWaarde,
        },
    });

    if (!data) return null;

    const onOpenChangeWrapper = (open: boolean) => {
        if (!open) {
            form.reset({ trajecten: actie.nieuweWaarde });
        }
        onOpenChange(open);
    };

    const reikwijdte = data.reikwijdte;

    const footer = (props: ActieDialogFooterProps<TrajectenOfReikwijdteGewijzigdActie>) => (
        <TrajectenOfReikwijdteGewijzigdActieDialogFooter {...props} handleSubmit={form.handleSubmit} />
    );

    return (
        <ActieDialog open={open} onOpenChange={onOpenChangeWrapper} dossierId={dossierId} actie={actie} footer={footer}>
            <Form {...form}>
                <div className="bg-elevated-sunken border-subtle rounded-md border p-2">
                    <h3 className="text-s font-bold">Huidige reikwijdte {reikwijdte !== "Landelijk" && `(${reikwijdte.toLowerCase()})`}</h3>
                    <p>{getReikwijdteLabel(reikwijdte, data.stations, data.trajecten)}</p>
                </div>
                <form>
                    <TrajectenEditor label="Nieuwe trajecten" />
                </form>
            </Form>
        </ActieDialog>
    );
}

type TrajectenOfReikwijdteGewijzigdActieDialogFooterProps = ActieDialogFooterProps<TrajectenOfReikwijdteGewijzigdActie> & {
    handleSubmit: UseFormHandleSubmit<NieuweTrajectenData>;
};

function TrajectenOfReikwijdteGewijzigdActieDialogFooter({
    actie,
    dossierId,
    handleSubmit,
    onClose,
}: TrajectenOfReikwijdteGewijzigdActieDialogFooterProps) {
    const { mutate: replaceActie, error, isPending } = useReplaceActie();
    const [weigerenPopupOpen, setWeigerenPopupOpen] = useState(false);

    const handleAcceptAction = ({ trajecten }: NieuweTrajectenData) => {
        replaceActie(
            {
                dossierId,
                actieId: actie.id,
                actie:
                    actie.type === "ReikwijdteGewijzigd"
                        ? ReikwijdteGewijzigdActie.toDto(ReikwijdteGewijzigdActie.update(actie, trajecten))
                        : TrajectenGewijzigdActie.toDto(TrajectenGewijzigdActie.update(actie, trajecten)),
            },
            { onSuccess: () => onClose() }
        );
    };

    return (
        <DialogFooter className="grid justify-stretch gap-2">
            <div className="flex w-full items-center justify-end gap-2">
                <Button
                    data-testid="decline-actie"
                    variant="tertiary"
                    destructive
                    onClick={() => setWeigerenPopupOpen(true)}
                    disabled={isPending}
                >
                    Weigeren
                </Button>
                <Button data-testid="confirm-actie" variant="primary" onClick={handleSubmit(handleAcceptAction)} disabled={isPending}>
                    Bevestigen
                </Button>
            </div>
            {error && <ErrorDisplay error={error} />}
            <DeclineActieConfirmation
                actie={actie}
                isOpen={weigerenPopupOpen}
                setIsOpen={setWeigerenPopupOpen}
                closeActieDialog={onClose}
                dossierId={dossierId}
            />
        </DialogFooter>
    );
}
